import { useParams } from 'react-router-dom';
import {
  makeStyles, tokens, Title2, Body1, Spinner,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { useVelocity } from '../../api/hooks';

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  // Many iterations → bars + labels keep their width and the chart scrolls horizontally
  // inside this wrapper rather than squashing off the page. Both the bar row and the label
  // row live inside the SAME scroller so they scroll together and stay aligned.
  scroll: { overflowX: 'auto', minWidth: 0 },
  chart: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'flex-end', height: '280px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  bar: {
    // grow 1 (few bars still fill the width); shrink 0 + fixed basis/min-width so many bars
    // stay legible and the chart scrolls instead of squashing.
    flex: '1 0 2.5rem',
    minWidth: '2.5rem',
    backgroundColor: tokens.colorBrandBackground,
    minHeight: '4px',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingBottom: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: `${tokens.borderRadiusMedium} ${tokens.borderRadiusMedium} 0 0`,
  },
  labelsRow: { display: 'flex', gap: tokens.spacingHorizontalS, marginTop: tokens.spacingVerticalS },
  // Same flex sizing as `bar` so each label stays under its bar when the chart scrolls.
  label: { flex: '1 0 2.5rem', minWidth: '2.5rem', textAlign: 'center', fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 },
});

export function VelocityPage() {
  const styles = useStyles();
  const { projectId } = useParams<{ projectId: string }>();
  const { data, isLoading, error } = useVelocity(projectId);

  if (isLoading) return <Spinner label="Loading velocity…" />;
  if (error) return <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>;
  if (!data || data.length === 0) {
    return <MessageBar intent="info"><MessageBarBody>No completed stories found for the recent iterations.</MessageBarBody></MessageBar>;
  }

  const max = Math.max(...data.map((d) => d.completedStoryPoints), 1);

  return (
    <div className={styles.page}>
      <Title2>Velocity</Title2>
      <Body1>Completed story points per iteration (last {data.length}).</Body1>
      <div className={styles.scroll}>
        <div className={styles.chart}>
          {data.map((d) => (
            <div key={d.iterationName} className={styles.bar} style={{ height: `${(d.completedStoryPoints / max) * 100}%` }}>
              {d.completedStoryPoints}
            </div>
          ))}
        </div>
        <div className={styles.labelsRow}>
          {data.map((d) => <div key={d.iterationName} className={styles.label}>{d.iterationName}</div>)}
        </div>
      </div>
    </div>
  );
}
